/**
 * useSelectPaymentCurrency Hook
 *
 * Selects payment currency for an order using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { Order } from "@/generated/graphql";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const SelectPaymentCurrencyDocument = `
  mutation SelectPaymentCurrency($orderId: ID!, $currency: String!) {
    selectPaymentCurrency(orderId: $orderId, currency: $currency) {
      id
      currency
      subtotal
      feesTotal
      totalAmount
      selectedPricing {
        id
        currency
        subtotal
        feesTotal
        total
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseSelectPaymentCurrencyResult {
  selectCurrency: (orderId: string, currency: string) => Promise<Order>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Select payment currency for an order.
 *
 * Uses URQL mutation for changing the payment currency.
 */
export function useSelectPaymentCurrency(): UseSelectPaymentCurrencyResult {
  const [result, executeMutation] = useMutation<
    { selectPaymentCurrency: Order },
    { orderId: string; currency: string }
  >(SelectPaymentCurrencyDocument);

  const selectCurrency = useCallback(
    async (orderId: string, currency: string): Promise<Order> => {
      try {
        const response = await executeMutation({ orderId, currency });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to select currency"
          );
        }

        if (!response.data?.selectPaymentCurrency) {
          throw new Error("No order returned from server");
        }

        errorTracking.addBreadcrumb({
          category: "payment",
          message: "Currency selected",
          data: { orderId, currency },
        });

        return response.data.selectPaymentCurrency;
      } catch (err) {
        const currencyError =
          err instanceof Error ? err : new Error("Failed to select currency");

        errorTracking.captureError(currencyError, {
          tags: { feature: "payment", action: "selectCurrency" },
          extra: { orderId, currency },
        });

        throw currencyError;
      }
    },
    [executeMutation]
  );

  return {
    selectCurrency,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
